// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  ReportQueryParams,
  ReportResponseItem,
} from "@suns/api/generated-client/apollo";
import { useEffect, useState } from "react";
import { playersReportsLoader } from "../../pages/players/loaders/player-loader";
import { useAsync } from "@/shared/hooks/useAsync";
import { useParams } from "react-router-dom";
import { SunsApiError } from "@suns/api";
import { ReportsList } from "@/components";
import { Filter, Switch, Text } from "@suns/design-system";
import { Flex } from "@suns/design-system";
import { allAuthorUsernamesLoader } from "@/pages/reports/reports-listings/loaders/all-reports-loader";

export interface Author {
  authorUsername: string;
  authorName: string;
}

export function EntityReportListings() {
  const PAGE_SIZE = 10;
  const { playerId } = useParams();
  const [publishedPage, setPublishedPage] = useState(0);
  const [reportType, setReportType] = useState<
    [ReportResponseItem.type.GENERAL, ReportResponseItem.type.PHASE] | "All"
  >([ReportResponseItem.type.GENERAL, ReportResponseItem.type.PHASE]);
  const [selectedReportAuthors, setSelectedReportAuthors] = useState<string[]>(
    []
  );

  const {
    loading: reportsLoading,
    response: reportsResponse,
    refresh: refreshReports,
    error: reportsError,
  } = useAsync(playersReportsLoader, {
    offset: publishedPage * PAGE_SIZE,
    limit: PAGE_SIZE,
    status: ReportQueryParams.status.PUBLISHED,
    playerId: playerId ? Number(playerId) : undefined,
    type:
      reportType === "All"
        ? undefined
        : [ReportResponseItem.type.PHASE, ReportResponseItem.type.GENERAL],
    authorUsername: selectedReportAuthors,
  });

  const {
    response: reportsAuthorsResponse,
    loading: reportsAuthorsLoading,
    error: reportsAuthorsError,
  } = useAsync(allAuthorUsernamesLoader);

  const reportsAuthors = reportsAuthorsResponse?.map((author: Author) => ({
    key: author.authorUsername,
    name: author.authorName,
  }));

  const handleSetPage = (page: number) => {
    setPublishedPage(page - 1);
  };

  const currentPublishedPage = Math.floor(
    (reportsResponse?.offset || 0) / (reportsResponse?.limit || 1)
  );

  function handleReportsFilterCheckboxChange() {
    setReportType((prev) =>
      prev !== "All"
        ? "All"
        : [ReportResponseItem.type.GENERAL, ReportResponseItem.type.PHASE]
    );
  }

  useEffect(() => {
    if (publishedPage !== currentPublishedPage) {
      window.scrollTo({ top: 0 });
      refreshReports();
    }
  }, [publishedPage, currentPublishedPage, refreshReports]);

  if (reportsError) {
    throw new SunsApiError("Error loading the published reports.", {
      cause: reportsError,
    });
  }

  if (reportsAuthorsError) {
    throw new SunsApiError("Error loading the report authors.", {
      cause: reportsAuthorsError,
    });
  }

  return (
    <>
      <Flex direction="right" align="center" gap="md" className="mb-4">
        <Flex direction="right" align="center" gap="sm">
          <Switch
            id="phase-mode"
            checked={reportType === "All"}
            onCheckedChange={handleReportsFilterCheckboxChange}
          />
          <Text size="xs">Show All Reports</Text>
        </Flex>
        <Filter
          avoidCollisions={false}
          title={reportsAuthorsLoading ? "Loading..." : "Author"}
          value={selectedReportAuthors || []}
          onChange={(value) => setSelectedReportAuthors(value as string[])}
          options={
            reportsAuthors?.map((author) => ({
              key: author.key,
              name: author.name,
            })) ?? []
          }
        />
      </Flex>
      <ReportsList
        loading={reportsLoading}
        response={
          reportsResponse || {
            reports: [],
            offset: 0,
            limit: PAGE_SIZE,
            count: 0,
            sortColumn: "createdAt",
            sortDir: "DESC",
          }
        }
        setPage={handleSetPage}
        hideListItemHeader={true}
      />
    </>
  );
}
